/* eslint-disable max-lines */
import { Component, OnInit, Input, Inject, ViewEncapsulation, ChangeDetectorRef, TemplateRef } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { zip, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, tap, take, switchMap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { CookieService } from 'ngx-cookie-service';

import { isNullOrUndefined } from '@app/util/util';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { AirValidationResponse, AirValidationResponseVm } from '@app/entities/air-validation.response';
import { TravelDetailProvider } from '@shared/flight-itinerary/entities/travel-detail-provider';
import { BookingFacade } from '@booking/booking.facade';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { BillingFacade } from '@billing/billing.facade';
import { PaxFacade } from '@pax/pax.facade';
import { PaxFormService } from '@pax/services/pax-form.service';
import { upsellDesktopSelectRoutes } from '@booking/mappers/upsell-desktop.mapper';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { FlightResultsCabinFacade } from '@flight-results/flight-results-cabin.facade';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { flightFreeCancellationBookingEnabled } from '@ux/mappers/free-cancellation.mapper';
import { UpsellFacade } from '@upsell/upsell.facade';
import {
  bookingUpsellRoute, BOOK_SEARCH_ID_PARAM, informativeBookingRoute, informativePaxCelebrateBookingRoute
} from '@booking/constants/booking-routes.constants';
import { LuggagePipe } from '@shared/luggage/pipes/luggage.pipe';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { UpsellBookingProps } from '@upsell/entities/upsell-booking-params';
import { UpsellResponse } from '@shared/flight-itinerary/entities/upsell-flights';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { FlightItem } from '@app/entities/flight-results';
import { AppFacade } from '@app/app.facade';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { AvoService } from '@avo/services/avo.service';
import { BookingFlightsBaseContainer } from '@booking/abstracts/booking-flights-base.container';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { flightAmplitudeProductPropertiesMapper } from '@app/modules/core/mappers/flights-amplitude.mapper';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { AVO_EVENTS } from '@avo/events/avo.events';

@Component({
  selector: 'app-booking-flights',
  templateUrl: './booking-flights.container.html',
  styleUrls: ['../booking-base.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class BookingFlightsContainer extends BookingFlightsBaseContainer implements OnInit {

  @Input() airValidation: AirValidationResponse;
  @Input() passengerTitleBlock!: TemplateRef<void>;

  public bookingFlightsForm: UntypedFormGroup;
  public flightSummaryProvider: TravelDetailProvider = TravelDetailProvider.BOOKING_FLIGHT;
  public adults: TravelerTypes = TravelerTypes.ADULTS;
  public children: TravelerTypes = TravelerTypes.CHILDREN;
  public infalts: TravelerTypes = TravelerTypes.INFANTS;
  public policiesType: SearchBy = SearchBy.FLIGHTS;
  public showUpsellsFormModal: boolean = false;
  public selectedRoutes: FlightItem[] = [];

  constructor(
    private readonly domManipulateService: DomManipulateService,
    private readonly flightsCabinFacade: FlightResultsCabinFacade,
    private readonly luggagePipe: LuggagePipe,
    private readonly upsellFacade: UpsellFacade,
    private readonly cookieService: CookieService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected facade: BookingFacade,
    protected viewportScroller: ViewportScroller,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    super(
      route, router, cdRef, appFacade, facade, viewportScroller, billingFacade, paxFacade, paxFormService, scrollFormService,
      propertiesAmplitudeService, bookingAmplitudeService, triggerAmplitudeService, assistanceFacade,
      fullValidTravelersPipe, avoService, travelerListVmPipe
    );
    this.travelerBookingProvider = TravelBookingType.FLIGHTS;
  }

  ngOnInit(): void {
    this.mappedValidationID = this.airValidation.ValidationId;
    this.amplitudeValidationResponse = this.airValidation;
    if (this.isBrowser) {
      super.ngOnInit();

      this.subscriptions.push(
        this.celebratePaxView$.pipe(
          distinctUntilChanged(),
          tap(celebratePaxView => {
            if (celebratePaxView) {
              this.router.navigate([{ outlets: { [informativeBookingRoute]: [informativePaxCelebrateBookingRoute] } }], {
                state: { celebratePaxView },
                relativeTo: this.route, skipLocationChange: true
              });
            }
          })
        ).subscribe()
      );

      this.configDefaultByInfoType(this.travelerBookingProvider, this.startDate);
      this.listenToAssistanceSearchCall(
        this.airValidation, this.airValidation?.TripOption?.Price?.CurrencyCode, this.route.snapshot.paramMap
      );
      this.listenUpsell();
      this.listenUpsellDesktop();
    }
  }

  get isLoadingFligthsUpsellDesktop$(): Observable<boolean> {
    return this.upsellFacade.isLoadingUpsellForDesktop$;
  }

  get fligthsUpsellDesktop$(): Observable<UpsellResponse> {
    return this.upsellFacade.upsellForDesktop$;
  }

  get isLoadingUpsell$(): Observable<boolean> {
    return this.upsellFacade.isLoadingUpsell$;
  }

  get freeCancellationValidate(): boolean {
    return flightFreeCancellationBookingEnabled(this.airValidation, this.airlineProvider);
  }

  get isLoadingFlightCabin$(): Observable<boolean> {
    return this.flightsCabinFacade.isLoadingFlightCabin$;
  }

  get airlineCode(): string {
    return this.airValidation?.TripOption?.Sectors[0]?.Flights[0]?.AirlineCode;
  }

  get airlineProvider(): string {
    return this.airValidation?.TripOption?.Provider;
  }

  get hasAirValidation(): boolean {
    return !isEmpty(this.airValidation);
  }

  get isUpsellEnabled(): boolean {
    return this.airValidation?.TripOption?.UpSellEnabled;
  }

  get upsellOptionId(): number {
    const optionId = this.route.snapshot.paramMap.get(UpsellBookingProps.upsellOptionId);
    return !isNaN(Number(optionId)) ? Number(optionId) : null;
  }

  get upsellSelected(): boolean {
    return !isNullOrUndefined(this.upsellOptionId) && !isNullOrUndefined(this.upsellId);
  }

  get validateBaggage(): boolean {
    return !!this.airValidation && !!this.airValidation?.TripOption?.Sectors && !!this.airValidation?.TripOption?.Sectors[0] &&
      !!this.airValidation?.TripOption?.Sectors[0].Flights && !!this.airValidation?.TripOption?.Sectors[0].Flights[0] &&
      !!this.airValidation?.TripOption?.Sectors[0].Flights[0].BaggageRestrictions;
  }

  get fareFamilies(): string | null {
    if (this.airValidation?.TripOption?.Sectors?.length <= 0) {
      return null;
    }
    return this.airValidation?.TripOption?.Sectors[0]?.Flights[0]?.FareFamilies;
  }

  get baggage(): boolean {
    if (this.validateBaggage) {
      const baggage = this.luggagePipe.transform(this.airValidation?.TripOption?.Sectors[0].Flights[0].BaggageRestrictions);
      return !isNullOrUndefined(baggage) && baggage !== 0 ? true : false;
    } else return false;
  }

  get startDate(): string {
    return this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0]?.DepartureDate;
  }

  private get upsellId(): string {
    const id = this.route.snapshot.paramMap.get(UpsellBookingProps.upsellId);
    return id ? id : null;
  }

  public checkUpsell(): void {
    this.upsellFacade.fetchUpsellOptionsFlights(
      Number(this.route.snapshot.paramMap.get('optionId')),
      this.route.snapshot.paramMap.get(BOOK_SEARCH_ID_PARAM),
      this.cookieService.get('SeamlessLogin'),
    );
    this.registerSelectUpsellOptions();
  }

  public itemNumber$(index: number, previewTypes: string[] = []): Observable<number> {
    return this.passengerArrangementsState$
      .pipe(
        map(arrangement => {
          let item = index + 1;
          previewTypes.forEach(type => {
            item += arrangement.find(info => info.id === type).quantity;
          });
          return item;
        })
      );
  }

  public passengerInfo$(type: string): Observable<number[]> {
    return this.passengerArrangementsState$.pipe(
      map(arrangement => new Array(arrangement.find(passenger => passenger.id === type).quantity).fill(0)));
  }

  public showFlightDetail(data: { validator: AirValidationResponseVm, sectorNumber: number }): void {
    zip(
      this.passengerArrangementsState$,
      this.flightClass$,
      this.isAnInternationalTrip$
    ).subscribe(([passengerArrangementsState, flightClass, isInternational]) => {
      this.router.navigate([this.bookingDetail],
        {
          state: {
            passengerArrangement: passengerArrangementsState,
            price: data.validator.TripOption.Price,
            flightClass: flightClass,
            provider: data.validator.TripOption.Provider,
            airValidationSector: data.validator.TripOption.Sectors,
            sectorSelected: data.sectorNumber,
            isInternational: isInternational,
            productReference: data.validator.TripOption.ProductReference,
          },
          relativeTo: this.route, skipLocationChange: true
        }
      );
    }).unsubscribe();
  }

  public openUpsellDesktop(): void {
    this.selectedRoutes = upsellDesktopSelectRoutes(this.airValidation?.TripOption?.Sectors);
    this.facade.selectFlightsForUpsell(
      this.selectedRoutes, this.cookieService.get('SeamlessLogin'),
      this.route.snapshot.paramMap.get(BOOK_SEARCH_ID_PARAM), this.airValidation?.TripOption?.OptionId
    );
    this.registerSelectUpsellOptions();
  }

  public toggleUpsellForm(show: boolean = false): void {
    this.showUpsellsFormModal = show;
    this.domManipulateService.setBodyScroll(!show);
  }

  private openUpsell(passengerLength: number): void {
    this.router.navigate([{ outlets: { 'terms': [bookingUpsellRoute] } }], {
      state: {
        OptionId: Number(this.route.snapshot.paramMap.get('optionId')),
        EventSource: EVENT_SOURCE.BOOKING,
        BasePrice: this.airValidation?.TripOption?.Price?.Total,
        FlightOptions: { options: this.airValidation?.TripOption?.Sectors, provider: this.airValidation?.TripOption?.Provider },
        passengersQuantity: passengerLength
      }, relativeTo: this.route, skipLocationChange: true
    });
  }

  private listenUpsell(): void {
    this.subscriptions.push(
      combineLatest([
        this.isLoadingUpsell$,
        this.upsellFacade.upsell$,
        this.passengerListUILength$
      ]).subscribe(([isLoading, data, passengerLength]) => {
        if (!isLoading && !isEmpty(data) && data.UpsellOptions) {
          this.openUpsell(passengerLength);
        }
      }));
  }

  private listenUpsellDesktop(): void {
    this.subscriptions.push(
      combineLatest([this.isLoadingFligthsUpsellDesktop$, this.fligthsUpsellDesktop$]).pipe(
        filter(([isLoadingFligthsUpsell, fligthsUpsell]) => !isLoadingFligthsUpsell &&
          !isEmpty(fligthsUpsell) && !!fligthsUpsell.UpsellOptions),
        map(([isLoadingFligthsUpsell, fligthsUpsell]) => ({ isLoadingFligthsUpsell, fligthsUpsell })),
        tap(() => {
          this.toggleUpsellForm(true);
        })).subscribe());
  }

  private registerSelectUpsellOptions(): void {
    const productProps = flightAmplitudeProductPropertiesMapper(this.airValidation.TripOption);
    this.propertiesAmplitudeService.searchProperties$(SearchBy.FLIGHTS).pipe(
      take(1),
      switchMap(searchProps => this.avoService.getEventTriggerProps$(
        AVO_EVENTS.SELECT_UPSELL_OPTIONS, null, searchProps, productProps
      ).pipe(
        take(1),
        tap(airTrigger => {
          this.AvoDispatch.selectUpsellOptions({
            ...this.avoService.avoBaseProperties(airTrigger),
            ...this.avoService.avoSearchProps(airTrigger?.SEARCH_PROPS),
            ...this.avoService.avoProductProps(airTrigger?.PRODUCT_PROPS),
          });
        })
      ))
    ).subscribe().unsubscribe();
  }
}
